/**
 * Situations loader — pure function over the static data barrel.
 *
 * Validation runs once at module load (validateLadders + validateSpeechActs).
 * Filtering by CEFR is sync (data is already in-bundle via static imports).
 */

import allSituations from '../data/situations/index.js';
import {
  KNOWN_SPEECH_ACTS,
  SITUATION_CATEGORIES,
  type SituationCard,
  type SituationCategory
} from './types.js';
import type { CEFRLevel, EnglishLevel } from '../types.js';

const CEFR_RANK: Record<CEFRLevel, number> = {
  A1: 1,
  A2: 2,
  B1: 3,
  B2: 4,
  C1: 5,
  C2: 6
};

/**
 * Asserts each `situationFamily` has at least 2 variants at distinct CEFR
 * levels (the ladder pattern). Throws in dev (vite import.meta.env.DEV);
 * console.warns in prod so a content typo never wipes out the deck for users.
 */
export function validateLadders(cards: readonly SituationCard[]): void {
  const families = new Map<string, Set<CEFRLevel>>();
  for (const card of cards) {
    if (!card.situationFamily) continue;
    const set = families.get(card.situationFamily) ?? new Set<CEFRLevel>();
    set.add(card.cefr);
    families.set(card.situationFamily, set);
  }

  const errors: string[] = [];
  for (const [family, levels] of families) {
    if (levels.size < 2) {
      errors.push(
        `Ladder family "${family}" has only ${levels.size} distinct CEFR ` +
          `level (${[...levels].join(', ') || 'none'}); need ≥2 to form a ladder.`
      );
    }
  }
  if (errors.length === 0) return;

  if (import.meta.env?.DEV) {
    throw new Error(
      'Ladder integrity errors:\n' + errors.map(e => `  • ${e}`).join('\n')
    );
  }
  console.warn('Ladder integrity warnings (running with stale content):', errors);
}

/**
 * Warns on `speechAct` values outside `KNOWN_SPEECH_ACTS`. Does NOT fail —
 * content authors may add novel values; promote frequently-used ones to the
 * curated array later.
 */
export function validateSpeechActs(cards: readonly SituationCard[]): void {
  const known = new Set<string>(KNOWN_SPEECH_ACTS);
  const unknown = new Set<string>();
  for (const card of cards) {
    if (card.speechAct && !known.has(card.speechAct)) {
      unknown.add(card.speechAct);
    }
  }
  if (unknown.size > 0) {
    console.warn(
      'Unknown speechAct values (consider adding to KNOWN_SPEECH_ACTS):',
      [...unknown]
    );
  }
}

// Run validators eagerly at module load. Catches typos before any
// session render.
validateLadders(allSituations);
validateSpeechActs(allSituations);

/**
 * Load Situations cards filtered by CEFR level (and optionally by category).
 * `Everyday` returns all cards regardless of CEFR (matches the existing
 * vocabulary-deck convention).
 */
export function loadSituationsDeck(
  level: EnglishLevel,
  categories?: readonly SituationCategory[]
): SituationCard[] {
  const cats = categories ?? SITUATION_CATEGORIES;
  const inCats = new Set<SituationCategory>(cats);
  const filtered = allSituations.filter(c => inCats.has(c.category));
  if (level === 'Everyday') return filtered;
  const maxRank = CEFR_RANK[level as CEFRLevel];
  return filtered.filter(c => CEFR_RANK[c.cefr] <= maxRank);
}

/** Counts per category in the full deck — useful for category picker badges. */
export function categoryCounts(): Record<SituationCategory, number> {
  const out = {} as Record<SituationCategory, number>;
  for (const cat of SITUATION_CATEGORIES) out[cat] = 0;
  for (const card of allSituations) out[card.category]++;
  return out;
}
